import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useSaveDocument, useDocument } from '../../api/useDocuments';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { toast } from 'sonner';

type DocumentSlug = 'privacy_policy' | 'cookie_policy' | 'note_legali';

const DOCUMENTS: { slug: DocumentSlug; title: string; description: string }[] = [
  { slug: 'privacy_policy', title: 'Privacy Policy', description: 'Informativa sulla privacy e trattamento dati' },
  { slug: 'cookie_policy', title: 'Cookie Policy', description: 'Informativa sui cookie e tracciamento' },
  { slug: 'note_legali', title: 'Note Legali', description: 'Condizioni generali e dati societari' },
];

function DocumentEditModal({ slug, onClose, onSuccess }: { slug: DocumentSlug; onClose: () => void; onSuccess: () => void }) {
  const { data } = useDocument(slug);
  const saveMutation = useSaveDocument();
  const [content, setContent] = useState('');
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (data && !loaded) {
      setContent(data.content);
      setLoaded(true);
    }
  }, [data, loaded]);

  const handleSave = async () => {
    try {
      await saveMutation.mutateAsync({ slug, content });
      toast.success('Documento salvato con successo');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore durante il salvataggio');
    }
  };

  const documentTitle = DOCUMENTS.find(d => d.slug === slug)?.title || slug;

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Modifica: ${documentTitle}`}
      width={800}
      footer={
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>Annulla</Button>
          <Button onClick={handleSave} loading={saveMutation.isPending}>Salva modifiche</Button>
        </div>
      }
    >
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '1.5rem', paddingBottom: '0.5rem' }}>
        <button
          onClick={() => setTab('edit')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            padding: '0.5rem 1rem',
            background: tab === 'edit' ? 'var(--color-primary-highlight)' : 'transparent',
            border: tab === 'edit' ? '1px solid rgba(79,152,163,0.3)' : '1px solid transparent',
            borderRadius: 'var(--radius-interactive)',
            cursor: 'pointer',
            color: tab === 'edit' ? 'var(--color-primary)' : 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)',
            fontWeight: tab === 'edit' ? 600 : 400,
            transition: 'all var(--spring-duration) var(--spring-easing)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Modifica
        </button>
        <button
          onClick={() => setTab('preview')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            padding: '0.5rem 1rem',
            background: tab === 'preview' ? 'var(--color-primary-highlight)' : 'transparent',
            border: tab === 'preview' ? '1px solid rgba(79,152,163,0.3)' : '1px solid transparent',
            borderRadius: 'var(--radius-interactive)',
            cursor: 'pointer',
            color: tab === 'preview' ? 'var(--color-primary)' : 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)',
            fontWeight: tab === 'preview' ? 600 : 400,
            transition: 'all var(--spring-duration) var(--spring-easing)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Preview
        </button>
      </div>

      {/* Tab Content */}
      {tab === 'edit' && (
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Inserisci il contenuto in markdown..."
          style={{
            width: '100%',
            minHeight: '400px',
            padding: '0.75rem',
            fontSize: 'var(--text-sm)',
            fontFamily: 'monospace',
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            color: 'var(--color-text)',
            resize: 'vertical',
            outline: 'none',
          }}
        />
      )}

      {tab === 'preview' && (
        <div
          style={{
            padding: '1rem',
            background: 'var(--glass-bg-card)',
            borderRadius: 'var(--radius-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            fontSize: 'var(--text-sm)',
            lineHeight: 1.6,
            color: 'var(--color-text)',
            minHeight: '400px',
          }}
        >
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: '1.25rem', marginBottom: '0.5rem' }}>
                  {children}
                </h2>
              ),
              p: ({ children }) => <p style={{ marginBottom: '1rem' }}>{children}</p>,
              ul: ({ children }) => (
                <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>{children}</ul>
              ),
              li: ({ children }) => <li style={{ marginBottom: '0.5rem' }}>{children}</li>,
              strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
              em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
              code: ({ children }) => (
                <code style={{
                  background: 'var(--glass-bg-card)',
                  padding: '0.1rem 0.3rem',
                  borderRadius: 'var(--radius-xs)',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                }}>{children}</code>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      )}
    </Modal>
  );
}

export function LegalDocumentsSection() {
  const [editingSlug, setEditingSlug] = useState<DocumentSlug | null>(null);

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '0.5rem' }}>
            Documenti Legali
          </h3>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Modifica i documenti legali mostrati agli utenti durante il login.
          </p>
        </div>

        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {DOCUMENTS.map(doc => (
            <div
              key={doc.slug}
              style={{
                background: 'var(--glass-bg-card)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-card)',
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <FileText size={18} style={{ color: 'var(--color-text-muted)' }} />
                <div>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text)' }}>
                    {doc.title}
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                    {doc.description}
                  </div>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditingSlug(doc.slug)}
              >
                Modifica
              </Button>
            </div>
          ))}
        </div>
      </div>

      {editingSlug && (
        <DocumentEditModal
          slug={editingSlug}
          onClose={() => setEditingSlug(null)}
          onSuccess={() => setEditingSlug(null)}
        />
      )}
    </>
  );
}
